/**
 * Photographs laid under a drawing, to trace a board from.
 *
 * The whole point is reverse engineering: photograph a board you have in your
 * hand, get the picture square and to the right size, and lay tracks and parts
 * over what you can see.
 *
 * The pixels live here and nowhere else. A document records only *where* a
 * picture goes — see {@link ImageRef} — so a board file stays a board file, and
 * a photograph is loaded afresh each session by the one person who has it. That
 * is also how the format has always had it, and it is why a board saved after
 * tracing does not arrive at a fab house with a snapshot of somebody's desk in
 * it.
 */

import type { CadDocument, ImageRef } from '@sircad/core/model.ts';

/**
 * Pixels by file name, for this session only.
 *
 * Keyed by the name the document records. Two boards tracing the same
 * photograph share one decoded copy, which matters when it is eight megabytes.
 */
const loaded = new Map<string, HTMLImageElement>();

/** The pixels for a reference, if this session has them. */
export function pixelsFor(ref: ImageRef): HTMLImageElement | undefined {
  return loaded.get(ref.name);
}

/** Every reference in the document that has pixels to draw. */
export function drawable(
  doc: CadDocument,
): { ref: ImageRef; pixels: HTMLImageElement }[] {
  const out: { ref: ImageRef; pixels: HTMLImageElement }[] = [];
  for (const ref of doc.images ?? []) {
    const pixels = loaded.get(ref.name);
    if (pixels) out.push({ ref, pixels });
  }
  return out;
}

/** References the document expects but this session has not been given. */
export function missing(doc: CadDocument): ImageRef[] {
  return (doc.images ?? []).filter((r) => !loaded.has(r.name));
}

/** Forget a picture's pixels. The reference, if any, is the caller's business. */
export function forget(name: string): void {
  loaded.delete(name);
}

/**
 * Decode a picked file and remember it under `as`.
 *
 * `as` is the name the document knows the picture by, which is not necessarily
 * the name of the file just chosen: a board recorded on another machine says
 * `C:\...\IMAG0669.jpg`, and the person reloading it picks `IMAG0669.jpg` from
 * wherever it lives now. Binding the pixels to the recorded name is what lets
 * the board find them again.
 */
export async function load(file: File, as = file.name): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error(`${file.name} is not a picture this browser can read`));
      el.src = url;
    });
    loaded.set(as, img);
    return img;
  } finally {
    // The decoded image keeps its own copy, so the blob URL has done its job.
    // Left alive it would pin the whole file in memory for the session.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/** Ask for a picture and decode it. Resolves to null if the picker is dismissed. */
export function pick(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', () => {
      resolve(input.files?.[0] ?? null);
      input.remove();
    });
    // A dismissed picker fires no event in most browsers; `cancel` is the one
    // that does, and where it is missing the promise simply never settles,
    // which for a dialog the user closed is the same as doing nothing.
    input.addEventListener('cancel', () => { resolve(null); input.remove(); });
    input.style.display = 'none';
    document.body.append(input);
    input.click();
  });
}

/**
 * A reference for a freshly placed picture.
 *
 * Placed with its lower-left corner at `at`, and sized by a first guess at the
 * resolution: a photograph carries no idea how big the thing it shows is, so
 * something has to be assumed and then corrected. Two hundred pixels to the
 * inch puts a typical phone photograph at about the size of a small board,
 * which is close enough to see and then set exactly.
 */
export function place(
  img: HTMLImageElement, name: string, at: { x: number; y: number },
): ImageRef {
  return {
    name,
    x: at.x,
    y: at.y,
    width: img.naturalWidth,
    height: img.naturalHeight,
    resolution: 1 / 200,
  };
}

/**
 * The resolution that makes two points on the picture the given distance apart.
 *
 * This is how a photograph is got to scale honestly: pick two features whose
 * separation you know — two pads of a tenth-inch header, the span of a DIP —
 * say what that distance is, and the scale follows. Guessing by eye at a corner
 * handle is quicker and is wrong by however much you misjudged, which is a
 * factor every track you then trace inherits.
 */
export function resolutionFrom(
  a: { x: number; y: number }, b: { x: number; y: number },
  knownDistance: number, ref: ImageRef,
): number | null {
  const apart = Math.hypot(b.x - a.x, b.y - a.y);
  if (!(apart > 0) || !(knownDistance > 0)) return null;
  // The two points were clicked in drawing units at the present scale, so the
  // correction is simply the ratio of what it should be to what it is.
  return ref.resolution * (knownDistance / apart);
}
